import { useEffect, useState, type ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { needsAiDataConsent } from '../../lib/aiDataApi.js';
import { isIOS, isNative } from '../../lib/platform.js';
import { checkPushPermission, requestPushPermission } from '../../lib/pushNotifications.js';
import {
  isPushPrePromptPending,
  markPushPrePromptAnswered,
} from '../../lib/pushPrePrompt.js';
import '../../pwa/pwa.css';

/**
 * "¿Te avisamos?" (ver `lib/pushPrePrompt.ts`): una vez, en Home, después de
 * la primera charla, sólo en la app nativa y sólo si el OS todavía no preguntó
 * (permiso 'prompt'). "Activar" dispara el prompt del sistema desde un gesto
 * del user, que es lo que el prompt one-shot necesita. Reusa el banner de
 * instalación de la PWA (`pwa-layer` / `pwa-banner`): mismo lugar, mismo
 * lenguaje visual, y en nativo la PWA no lo muestra nunca.
 *
 * iOS (App Review 5.1.1(iv), the rejection Club Arena got): a screen shown
 * before the system permission prompt may not offer a way to skip it. There the
 * banner has a single «Continuar» that always opens the system dialog — the
 * person says yes or no THERE. Android keeps «Ahora no» / «Activar». The copy
 * covers every push the backend sends (streak reminders and the come-back
 * nudges of `jobs/push-winback.ts`) and where to turn them off (Perfil).
 *
 * Never while the AI data consent is pending: this banner is mounted outside
 * `AiConsentGate` (it is app-wide, next to the other bridges), so without this
 * check it would float over the consent step and let the person reach the
 * system dialog before consenting. It does not consume its one-shot state in
 * that case either — it simply waits until the consent is accepted.
 */
export function PushPrePrompt(): ReactElement | null {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const onHome = pathname === '/home';
  const consentPending = user ? needsAiDataConsent(user) : false;
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!isNative || !user || consentPending || !onHome || !isPushPrePromptPending()) {
      setShow(false);
      return;
    }
    let alive = true;
    void checkPushPermission()
      .then((permission) => {
        if (!alive) return;
        // Ya concedido o ya negado: el sistema decidió, no hay nada que ofrecer.
        if (permission !== 'prompt') {
          markPushPrePromptAnswered();
          return;
        }
        setShow(true);
      })
      .catch((err: unknown) => {
        console.warn('[push] no se pudo leer el permiso para el pre-prompt', err);
      });
    return () => {
      alive = false;
    };
  }, [user, consentPending, onHome]);

  if (!show || consentPending) return null;

  const close = (): void => {
    markPushPrePromptAnswered();
    setLeaving(true);
    window.setTimeout(() => setShow(false), 220);
  };

  // Single-button variant: see the header (5.1.1(iv)).
  const iosNative = isIOS();

  const activate = (): void => {
    if (busy) return;
    setBusy(true);
    void requestPushPermission()
      .catch((err: unknown) => {
        console.warn('[push] el pedido de permiso falló', err);
      })
      .finally(() => {
        setBusy(false);
        close();
      });
  };

  return (
    <div className="pwa-layer">
      <div
        className={`pwa-banner ${leaving ? 'pwa-banner--out' : ''}`}
        role="dialog"
        aria-label="Activar notificaciones"
      >
        <div className="pwa-banner__icon" aria-hidden="true" style={{ color: 'var(--_lime)' }}>
          <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9 a 6 6 0 0 1 12 0 c 0 5 2 6 2 7 H 4 c 0 -1 2 -2 2 -7" />
            <path d="M10.5 19.5 a 1.8 1.8 0 0 0 3 0" />
          </svg>
        </div>
        <div className="pwa-banner__copy">
          <div className="pwa-banner__title">¿Te avisamos?</div>
          <div className="pwa-banner__sub">
            Recordatorios para practicar y no cortar tu racha. Podés apagarlos en Perfil.
          </div>
        </div>
        <div className="pwa-banner__actions">
          {iosNative ? (
            <button type="button" className="pwa-btn pwa-btn--primary" onClick={activate} disabled={busy}>
              Continuar
            </button>
          ) : (
            <>
              <button type="button" className="pwa-btn pwa-btn--ghost" onClick={close} disabled={busy}>
                Ahora no
              </button>
              <button type="button" className="pwa-btn pwa-btn--primary" onClick={activate} disabled={busy}>
                Activar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
